import React from 'react';
import _ from 'underscore';
import { Messages } from '../../codegen/Localize';
import { L10n } from '../../ts/L10n/L10n';
import { IBulkImportUsersFromCsvResult, IImportUserFromCSVError, IImportUserFromCSVResult } from '../../ts/ServerApi/types/BulkImportUsersFromCsvResponse';
import { UserActionResultCode } from '../services/UserActionResultCode';
import { BulkImportUserDialogStyle } from './styles/BulkImportUserDialogStyle';

export type BulkImportResultsSummaryProps = {
  readonly bulkImportUsersResult: IBulkImportUsersFromCsvResult;
};

export interface CodeUsernamesCount {code: number; usernames: string; count: number; message?: string;}
export interface CodeUsernamesCountMessage extends CodeUsernamesCount { message: string; }
export interface ErrorWithLocalizedMessage extends IImportUserFromCSVError { message: string; }

export const SUCCESS_RESULTS_TEST_ID = 'successResults';
export const ERROR_RESULTS_TEST_ID = 'errorResults';

export function BulkImportResultsSummary(props: BulkImportResultsSummaryProps): JSX.Element {
  const bulkImportUsersResult: IBulkImportUsersFromCsvResult = props.bulkImportUsersResult;

  const skippedLineCount = bulkImportUsersResult.skippedLineCount;
  const usersSkippedMessage = Messages.userActionBulkOperationNumberSkipped(
    {COUNT: skippedLineCount, COUNT_LOC: L10n.localizedCount(skippedLineCount)});

  const processedLineCount = bulkImportUsersResult.processedLineCount;
  const usersProcessedMessage = Messages.userActionBulkOperationNumberProcessed(
    {COUNT: processedLineCount, COUNT_LOC: L10n.localizedCount(processedLineCount)});

  const successResultsByCode = aggregateByResultCode(bulkImportUsersResult.results);
  const successResults = addLocalizedResultMessage(successResultsByCode);

  const errorResultsByCode = aggregateByErrorCode(bulkImportUsersResult.errors);
  const errorResults = addLocalizedErrorMessage(errorResultsByCode);

  return (
    <div>
      <div className="tb-bulk-import-results-summary">
        <div className={BulkImportUserDialogStyle.titleText}>{Messages.importComplete()}</div>
        <div className={BulkImportUserDialogStyle.summaryText}>{usersSkippedMessage}</div>
        <div className={BulkImportUserDialogStyle.summaryText}>{usersProcessedMessage}</div>
      </div>
      <div className={BulkImportUserDialogStyle.resultBody}>
        {successResults && createBulkResultsTable(successResults, SUCCESS_RESULTS_TEST_ID)}
        {errorResults && createBulkResultsTable(errorResults, ERROR_RESULTS_TEST_ID)}
      </div>
    </div>
  );
}

function createBulkResultsTable(results: CodeUsernamesCountMessage[], testId: string): JSX.Element {
  return(
    <div className="tb-enable-selection" data-tb-test-id={testId}>
      <table>
        {createBulkResultsRows(results)}
      </table>
    </div>);
}

function createBulkResultsRows(results: CodeUsernamesCountMessage[]): JSX.Element[] {
  return results.map((result) => {
    return (
      <tr key={result.code}>
        <td className={BulkImportUserDialogStyle.resultMessage}>{result.message}</td>
        <td className={BulkImportUserDialogStyle.resultUsername}>{result.usernames}</td>
      </tr>
    );
  });
}

function aggregateByResultCode(results: IImportUserFromCSVResult[]): _.Dictionary<CodeUsernamesCount> {
  const successResultsByCode: _.Dictionary<CodeUsernamesCount> = {};
  // aggregate success results by result code
  _.each(results, (user: IImportUserFromCSVResult) => {
    _.each(user.codes, (code: number) => {
      if (!successResultsByCode[code]) {
        successResultsByCode[code] = {
          code: code,
          usernames: user.username,
          count: 1
        };
      } else {
        successResultsByCode[code].usernames += ', ' + user.username;
        ++successResultsByCode[code].count;
      }
    });
  });
  return successResultsByCode;
}

function addLocalizedResultMessage(aggregatedResults: _.Dictionary<CodeUsernamesCount>): CodeUsernamesCountMessage[] {
  return _.map(aggregatedResults, (value: CodeUsernamesCount) =>
    _.extend(value, {
      message: UserActionResultCode.instance.toLocalizedResultMessage(value.code, value.count)
    })
  );
}

function addLocalizedErrorMessage(aggregatedResults: _.Dictionary<CodeUsernamesCount>): CodeUsernamesCountMessage[] {
  return _.map(aggregatedResults, (value: CodeUsernamesCount) =>
    _.extend(value, {
      message: UserActionResultCode.instance.toLocalizedErrorMessage(value.code, value.count, getSiteRole(value.message))
    })
  );
}

function getSiteRole(message: string) {
  if(message && message.match('role')){
    return message.split(':')[1].split('(')[0];
  }
}

function aggregateByErrorCode(errors: IImportUserFromCSVError[]): _.Dictionary<CodeUsernamesCount> {
  const errorResultsByCode: _.Dictionary<CodeUsernamesCount> = {};
  _.each(errors, (error: IImportUserFromCSVError) => {
    if (!error.username) {
      // for some errors (like UsernameMissing) there is no username.
      // So, show the line number were we would usually show the username.
      error.username = Messages.userActionBulkOperationErrorLineNumber({ LINE_NUMBER_LOC: L10n.localizedCount(error.line)});
    }
    const code = error.errorCode;
    if (!errorResultsByCode[code]) {
      errorResultsByCode[code] = {
        code: code,
        usernames: error.username,
        count: 1,
        message: error.message,
      };
    } else {
      errorResultsByCode[code].usernames += ', ' + error.username;
      ++errorResultsByCode[code].count;
    }
  });
  return errorResultsByCode;
}
